import { createTrackingConfig } from '../../../../components/generic/labels';
import { getDateFormat, tableConfig, uiConfig } from '../../../../config/appConfig';

export const capaActionConfig = {
  entityName: 'CapaAction',
  
  // Entity information for parent relationship
  entityTypeId: 3, // CAPA entity type ID (inherited from parent CAPA)
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/capa-action',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      bulkDelete: '/bulk-delete',
      // Get actions by CAPA ID
      byCapaId: (capaId) => `/capa/${capaId}/actions`
    },
    lookupEndpoints: {
      capaActionTypes: '/capa-type',
      users: '/capa/relations/users',
      priorities: '/priority',
      capaActionStatuses: '/capa-action-status'
    }
  },
  
  // UI Configuration
  ui: {
    modalWidth: '700px',
    modalHeight: '800px',
    modalMaxHeight: '80vh',
    requiredFieldStyle: uiConfig.requiredFieldStyle,
  },

  // Features configuration
  features: {
    fileAttachments: false, // Actions don't have file attachments for now
  },
  
  // Display Configuration
  displayName: 'CAPA Action',
  description: 'Corrective and Preventive Action item management',
  icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
  
  // Fields to remove from forms (relations, computed, system fields)
  fieldsToRemove: [
    'id', 'capaId', 'capa', 'type', 'user', 'priority', 'status',
    'verifiedBy', 'createdBy', 'updatedBy', 'createdAt', 'updatedAt',
    'attachments'
  ],

  // UNIFIED FIELD CONFIGURATION
  fields: [
    { 
      name: 'id', 
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    {
      name: 'description',
      label: 'Action Description',
      type: 'textarea',
      required: true,
      placeholder: 'Enter action description',
      rows: 8,
      table: { header: 'Description', width: '300px', sortable: true },
      filterable: false,
      validation: {
        required: true,
        maxLength: 1000,
        message: 'Action description is required and must not exceed 1000 characters'
      }
    },
    {
      name: 'typeId',
      label: 'Action Type',
      type: 'select',
      required: false,
      lookupEndpoint: 'capaActionTypes',
      options: [],
      table: { 
        header: 'Type', 
        width: '130px', 
        sortable: true,
        displayField: 'type.name'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid action type'
      }
    },
    {
      name: 'assignedToId',
      label: 'Assigned To',
      type: 'select',
      required: false,
      lookupEndpoint: 'users',
      options: [],
      table: { 
        header: 'Assigned To', 
        width: '150px', 
        sortable: true,
        displayField: 'user'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid user'
      }
    },
    {
      name: 'priorityId',
      label: 'Priority',
      type: 'select',
      required: false,
      lookupEndpoint: 'priorities',
      options: [],
      table: { 
        header: 'Priority', 
        width: '100px', 
        sortable: true,
        displayField: 'priority'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid priority'
      }
    },
    {
      name: 'dueDate',
      label: 'Due Date',
      type: 'date',
      required: false,
      table: { 
        header: 'Due Date', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange',
      validation: {
        message: 'Please enter a valid due date'
      }
    },

    {
      name: 'completedAt',
      label: 'Completed Date',
      type: 'date',
      required: false,
      readonly: true,
      table: { 
        header: 'Completed Date', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange'
    },
    {
      name: 'verifiedAt',
      label: 'Verified Date',
      type: 'date',
      required: false,
      readonly: true,
      table: { 
        header: 'Verified Date', 
        width: tableConfig.dateColumnWidth,
        sortable: true,
        cellRenderer: 'date',
        dateFormat: getDateFormat('table')
      },
      filterable: true,
      filterType: 'dateRange'
    },
    {
      name: 'effectivenessCheck',
      label: 'Effectiveness Check',
      type: 'textarea',
      required: false,
      placeholder: 'Describe the effectiveness of this action',
      rows: 6,
      table: { header: 'Effectiveness', width: '280px', sortable: true },
      filterable: false,
      validation: {
        maxLength: 1000,
        message: 'Effectiveness check must not exceed 1000 characters'
      }
    },
    {
      name: 'estimatedCost',
      label: 'Estimated Cost',
      type: 'number',
      required: false,
      step: '0.01',
      table: { header: 'Estimated Cost', width: '120px', sortable: true },
      filterable: true,
      validation: {
        min: 0,
        message: 'Estimated cost must be a positive number'
      }
    },
    {
      name: 'actualCost',
      label: 'Actual Cost',
      type: 'number',
      required: false,
      step: '0.01',
      table: { header: 'Actual Cost', width: '120px', sortable: true },
      filterable: true,
      validation: {
        min: 0,
        message: 'Actual cost must be a positive number'
      }
    },
    {
      name: 'statusId',
      label: 'Status',
      type: 'select',
      required: false,
      lookupEndpoint: 'capaActionStatuses',
      options: [],
      table: { 
        header: 'Status', 
        width: '120px', 
        sortable: true,
        displayField: 'status',
        cellRenderer: 'statusBadge'
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: {
        integer: true,
        message: 'Please select a valid status'
      }
    },
    {
      name: 'notes',
      label: 'Notes',
      type: 'textarea',
      required: false,
      placeholder: 'Additional notes about this action',
      rows: 6,
      table: { header: 'Notes', width: '280px', sortable: true },
      filterable: false,
      validation: {
        maxLength: 1000,
        message: 'Notes must not exceed 1000 characters'
      }
    },
    {
      name: 'evidenceRequired',
      label: 'Evidence Required',
      type: 'switch',
      required: false,
      defaultValue: false,
      switchLabel: 'Evidence Required', 
      description: 'Mark if evidence is required for this action', 
      labelPosition: 'left',
      table: { 
        header: 'Evidence Required', 
        width: '140px',
        sortable: true,
        cellRenderer: 'booleanBadge'
      },
      filterable: true,
      filterType: 'boolean',
      validation: {
        message: 'Please select evidence requirement'
      }
    },
    {
      name: 'evidenceProvided',
      label: 'Evidence Provided',
      type: 'switch',
      required: false,
      defaultValue: false,
      switchLabel: 'Evidence Provided', 
      description: 'Mark if evidence has been provided', 
      labelPosition: 'left',
      table: { 
        header: 'Evidence Provided', 
        width: '140px',
        sortable: true,
        cellRenderer: 'booleanBadge'
      },
      filterable: true,
      filterType: 'boolean',
      validation: {
        message: 'Please select evidence provision status'
      }
    },
    // Additional fields for layout display (these are the related objects from backend)
    {
      name: 'type',
      label: 'Action Type',
      type: 'text',
      table: { 
        header: 'Type', 
        width: '130px', 
        sortable: true,
        displayField: 'type.name'
      },
      filterable: false
    },
    {
      name: 'user',
      label: 'Assigned To',
      type: 'text',
      table: { 
        header: 'Assigned To', 
        width: '150px', 
        sortable: true,
        displayField: 'user.userDisplayName'
      },
      filterable: false
    },
    {
      name: 'priority',
      label: 'Priority',
      type: 'text',
      table: { 
        header: 'Priority', 
        width: '100px', 
        sortable: true,
        displayField: 'priority.name'
      },
      filterable: false
    },
    {
      name: 'status',
      label: 'Status',
      type: 'text',
      table: { 
        header: 'Status', 
        width: '120px', 
        sortable: true,
        displayField: 'status.name',
        cellRenderer: 'statusBadge'
      },
      filterable: false
    }
  ],

  // COMPUTED CONFIGURATIONS
  get tableColumns() {
    const columns = this.fields
      .filter(field => field.table !== false && field.table)
      .map(field => ({
        key: field.table.displayField || field.name,
        header: field.table.header || field.label,
        width: field.table.width || 'auto',
        sortable: field.table.sortable !== false,
        displayField: field.table.displayField || field.name,
        cellRenderer: field.table.cellRenderer || 'default',
        originalField: field.name
      }));
    
    return columns;
  },

  // LAYOUT CONFIGURATION - Define exactly where each field goes
  layout: {
    // Fields that go to the header (title area)
    headerFields: ['description'],
    
    // Grid layout configuration with conditional display
    grid: {
      // Column 1 configuration
      column1: {
        row1: 'type',        // Type goes in column 1, row 1
        row2: 'user',        // Assigned To goes in column 1, row 2
        row3: 'dueDate'      // Due Date goes in column 1, row 3
      },
      // Column 2 configuration  
      column2: {
        row1: 'priority',    // Priority goes in column 2, row 1
        row2: 'status'       // Status goes in column 2, row 2
      }
    },
    
    // Fields that appear as status badges (right side)
    statusBadges: []
  },

  // Backend field mappings
  fieldMappings: {
    type: 'type.name',
    user: 'user.userDisplayName',
    priority: 'priority.name',
    status: 'status.name',
    verifiedBy: 'verifiedBy.userDisplayName',
    createdBy: 'createdBy.userDisplayName',
    updatedBy: 'updatedBy.userDisplayName'
  },
  
  get filterableFields() {
    return this.fields
      .filter(field => field.filterable === true)
      .map(field => field.name);
  },

  get validationRules() {
    const rules = {};
    this.fields.forEach(field => {
      if (field.validation) {
        rules[field.name] = field.validation;
      }
    });
    return rules;
  },

  // Form Layout Configuration
  formSections: [
    {
      title: 'Action Details',
      icon: '📝',
      layout: 'grid',
      gridCols: 'md:grid-cols-1',
      fields: ['description']
    },
    {
      title: 'Assignment & Classification',
      icon: '👥',
      layout: 'grid',
      gridCols: 'md:grid-cols-3',
      fields: ['typeId', 'assignedToId', 'priorityId']
    },
    {
      title: 'Timeline',
      icon: '📅',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['dueDate', 'completedAt']
    },
    {
      title: 'Status & Completion',
      icon: '✅',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['statusId', 'verifiedAt']
    },
    {
      title: 'Effectiveness & Verification',
      icon: '🔍',
      layout: 'grid',
      gridCols: 'md:grid-cols-1',
      fields: ['effectivenessCheck']
    },
    {
      title: 'Financial Information',
      icon: '💰',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['estimatedCost', 'actualCost']
    },
    {
      title: 'Evidence & Notes',
      icon: '📋',
      layout: 'grid',
      gridCols: 'md:grid-cols-3',
      fields: ['evidenceRequired', 'evidenceProvided', 'notes'],
      fieldSpans: {
        'evidenceRequired': 'md:col-span-1',
        'evidenceProvided': 'md:col-span-1',
        'notes': 'md:col-span-3'
      }
    }
  ],

  // Tracking Section Configuration
  trackingSection: createTrackingConfig('CapaAction'),

  // Labels and Text Configuration
  labels: {
    singular: 'CAPA Action',
    plural: 'CAPA Actions',
    pageTitle: 'CAPA Action Management',
    pageSubtitle: 'Manage corrective and preventive action items',
    searchPlaceholder: 'Search actions by description...',
    viewTitle: 'View CAPA Action',
    noSelectionText: 'Select an action to see details',
    addNew: 'Add New Action',
    editAction: 'Edit Action'
  },

  // Data transformation function
  transformData: (data) => {
    console.log('capaActionConfig.transformData - Input data:', data);
    const transformed = { ...data };
    
    // Remove fields that should not be sent to the backend
    const fieldsToRemove = [
      'id', 'capaId', 'capa', 'type', 'user', 'priority', 'status',
      'verifiedBy', 'createdBy', 'updatedBy', 'createdAt', 'updatedAt',
      'attachments', 'createdById', 'updatedById', 'completed'
    ];
    
    console.log('capaActionConfig.transformData - Fields to remove:', fieldsToRemove);
    
    fieldsToRemove.forEach(field => {
      if (transformed.hasOwnProperty(field)) {
        console.log('capaActionConfig.transformData - Removing field:', field);
        delete transformed[field];
      }
    });
    
    console.log('capaActionConfig.transformData - After removing fields:', transformed);
    
    // Convert foreign key fields to integers or remove if empty
    const foreignKeyFields = [
      'typeId', 'assignedToId', 'priorityId', 'statusId'
    ];
    
    foreignKeyFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          const numValue = parseInt(transformed[field], 10);
          if (!isNaN(numValue)) {
            transformed[field] = numValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });

    // Convert decimal fields
    const decimalFields = ['estimatedCost', 'actualCost'];
    decimalFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          const numValue = parseFloat(transformed[field]);
          if (!isNaN(numValue)) {
            transformed[field] = numValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });

    // Convert date fields to proper Date objects
    const dateFields = ['dueDate', 'completedAt', 'verifiedAt'];
    
    dateFields.forEach(field => {
      if (transformed[field] !== undefined) {
        if (transformed[field] === '' || transformed[field] === null) {
          delete transformed[field];
        } else {
          const dateValue = new Date(transformed[field]);
          if (!isNaN(dateValue.getTime())) {
            transformed[field] = dateValue;
          } else {
            delete transformed[field];
          }
        }
      }
    });
    
    return transformed;
  },

  // Notification messages
  messages: {
    loading: 'Loading CAPA actions...',
    noData: 'No CAPA actions found.',
    createSuccess: 'CAPA action created successfully!',
    updateSuccess: 'CAPA action updated successfully!',
    deleteSuccess: 'CAPA action deleted successfully!',
    createError: 'Failed to create CAPA action',
    updateError: 'Failed to update CAPA action', 
    deleteError: 'Failed to delete CAPA action',
    loadError: 'Failed to load CAPA actions',
    validationError: 'Validation failed'
  },

  // Validation function for real-time field validation
  validateField: null // Will be set after validation module loads
};
  

export default capaActionConfig;
